import { Button, FormControl, HorizontalStack, Input, Modal, Text, Textarea } from '@lego/klik-ui';
import { type ChangeEvent, useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/hooks/api';
import { useCreateRestrictedKeyRequest } from '@/hooks/application';
import type { CreateRestrictedKeyRequestRequest } from '@/hooks/application/restricted_key_requests_resource';

const JUSTIFICATION_MIN_LENGTH = 10;

const PROPERTY_OPTIONS = [
  { value: '', label: 'Select a property...' },
  { value: 'build', label: 'build' },
  { value: 'privileged', label: 'privileged' },
  { value: 'pull_policy', label: 'pull_policy' },
  { value: 'cap_add', label: 'cap_add (requires capability)' },
] as const;

type Props = {
  workspaceId: number;
  applicationId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export const CreateRestrictedKeyRequestDialog = ({
  workspaceId,
  applicationId,
  isOpen,
  onClose,
  onSuccess,
}: Props) => {
  const [property, setProperty] = useState('');
  const [capability, setCapability] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const createRequest = useCreateRestrictedKeyRequest(workspaceId, applicationId, (err) =>
    setError(getApiErrorMessage(err, 'Failed to create restricted key request'))
  );

  useEffect(() => {
    if (isOpen) {
      setProperty('');
      setCapability('');
      setRequestReason('');
      setError(undefined);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const trimmedCapability = capability.trim();
  const trimmedReason = requestReason.trim();

  const isCapAdd = property === 'cap_add';
  const capabilityMissing = isCapAdd && trimmedCapability.length === 0;
  const reasonTooShort =
    trimmedReason.length > 0 && trimmedReason.length < JUSTIFICATION_MIN_LENGTH;

  const canSubmit =
    property !== '' &&
    trimmedReason.length >= JUSTIFICATION_MIN_LENGTH &&
    (!isCapAdd || trimmedCapability.length > 0) &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(undefined);

    const data: CreateRestrictedKeyRequestRequest = isCapAdd
      ? { property: 'cap_add', capability: trimmedCapability, requestReason: trimmedReason }
      : {
          property: property as 'build' | 'privileged' | 'pull_policy',
          requestReason: trimmedReason,
        };

    try {
      await createRequest(data);
      onClose();
      onSuccess?.();
    } catch {
      // Already toasted and logged by the mutation hook (setError above
      // handles the inline form error); caught only to avoid an unhandled
      // promise rejection here.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Request Restricted Property Access</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <FormControl isRequired>
            <FormControl.Label>Property</FormControl.Label>
            <select
              onChange={(e) => setProperty(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid',
                borderColor: 'inherit',
                fontSize: '14px',
              }}
              value={property}
            >
              {PROPERTY_OPTIONS.map((opt) => (
                <option disabled={opt.value === ''} key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FormControl>
          {isCapAdd && (
            <FormControl isInvalid={capabilityMissing} isRequired mt={4}>
              <FormControl.Label>Capability</FormControl.Label>
              <Input
                onChange={(e) => setCapability(e.target.value)}
                placeholder="e.g. CAP_NET_ADMIN, CAP_SYS_PTRACE"
                value={capability}
              />
              <FormControl.HelperText>
                The specific Linux capability to request
              </FormControl.HelperText>
            </FormControl>
          )}
          <FormControl isInvalid={reasonTooShort} isRequired mt={4}>
            <FormControl.Label>Justification</FormControl.Label>
            <Textarea
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setRequestReason(e.target.value)}
              placeholder="Explain why this restricted property is needed"
              value={requestReason}
            />
            {reasonTooShort && (
              <FormControl.ErrorText>
                Justification must be at least {JUSTIFICATION_MIN_LENGTH} characters
              </FormControl.ErrorText>
            )}
          </FormControl>
          {error && (
            <Text color="error.500" fontSize="sm" mt={4}>
              {error}
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <HorizontalStack spacing={2}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button isDisabled={!canSubmit} isLoading={isSubmitting} onClick={handleSubmit}>
              Submit Request
            </Button>
          </HorizontalStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
